import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import DataManagement from "../../components/table/DataManagement.jsx";
import { Link, useNavigate } from "react-router-dom";

import { useComprobantes } from "../../hooks_cache/useComprobantes.js";
import { toast } from "react-toastify";
import api from "../../config/axiosConfig.js";
import StatusBadge from "../../components/StatusBadge";
import Swal from "sweetalert2";

const numeroControlColumn = {
  accessorKey: "numeroControl",
  id: "numeroControl",
  header: "Numero de Control",
  cell: ({ row }) => {
    const { id, numeroControl } = row.original;
    return (
      <Link to={`/venta/comprobante/${id}`} className="fw-bold text-nowrap">
        {numeroControl}
      </Link>
    );
  },
};

const columns = [
  numeroControlColumn,
  { accessorKey: "Tercero.nombre", header: "Nombre / Razon social" },
  { accessorKey: "codigoGeneracion", header: "Codigo de generacion" },
  { accessorKey: "fecEmi", header: "Fecha de emision" },
  { accessorKey: "TipoDTE.descripcion", header: "Tipo de DTE" },
  {
    accessorKey: "totalPagar",
    header: "Importe",
    cell: ({ getValue }) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(getValue()),
  },
  {
    accessorKey: "pendiente",
    header: "Pendiente",
    cell: ({ getValue }) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(getValue()),
  },
  {
    accessorKey: "estado",
    header: "Estado",
    cell: ({ getValue }) => <StatusBadge estado={getValue()} size="" />,
  },
];

function ComprobantesEmitidos({ solo_notas_emitidas }) {
  const navigate = useNavigate();
  const { data, refetch, isLoading, isFetching } = useComprobantes();

  useEffect(() => {
    if (isFetching) {
      Swal.fire({
        title: "Cargando comprobantes emitidos...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    } else {
      Swal.close();
    }

    return () => {
      console.log("Limpiando el efecto de carga");
      Swal.close();
    };
  }, [isFetching]);

  const comprobantes = isLoading
    ? []
    : solo_notas_emitidas
    ? data?.filter((c) => (c.TipoDTE?.codigo === "05" || c.TipoDTE?.codigo === "06") && c.naturaleza === "venta")
    : data?.filter((c) => c.TipoDTE?.codigo !== "05" && c.TipoDTE?.codigo !== "06" && c.naturaleza === "venta");

  const [comprobantesSeleccionados, setComprobantesSeleccionados] = useState([]);
  const [rowSelection, setRowSelection] = useState({});

  const handleRowSelectionChange = (newSelection) => {
    setRowSelection(newSelection);

    const idsSeleccionados = Object.keys(newSelection).map(Number);
    const seleccionados = comprobantes.filter((c) => idsSeleccionados.includes(c.id));

    setComprobantesSeleccionados(seleccionados);
  };

  const handleAplicarAbono = () => {
    if (comprobantesSeleccionados.length === 0) {
      toast.warning("Debe seleccionar al menos un comprobante.");
      return;
    }

    const contieneTipo05 = comprobantesSeleccionados.some((c) => c.TipoDTE?.codigo === "05");
    if (contieneTipo05) {
      toast.warning("No se puede abonar a comprobantes con Tipo DTE -> Nota de Credito.");
      return;
    }

    const contieneInvalidado = comprobantesSeleccionados.some((c) => c.estado === "invalidada");
    if (contieneInvalidado) {
      toast.warning("No se puede abonar a comprobantes que están invalidados.");
      return;
    }

    const idsTerceros = new Set(comprobantesSeleccionados.map((c) => c.Tercero?.id));
    if (idsTerceros.size > 1) {
      toast.warning("Todos los comprobantes deben pertenecer al mismo cliente.");
      return;
    }

    const ids = comprobantesSeleccionados.map((c) => c.id).join(",");
    navigate(`/venta/pago?ids_comprobantes=${ids}`);
  };

  const imprimirPdfs = async () => {
    if (comprobantesSeleccionados.length === 0) {
      toast.warning("Debe seleccionar al menos un comprobante.");
      return;
    }

    const idsSelecteds = Object.entries(rowSelection)
      .filter(([_, value]) => value)
      .map(([key]) => Number(key));

    const { data } = await api.get(`/dte/print_dte_bulk/${idsSelecteds}`);

    const uniHtml = data.join('<div style="page-break-after: always;"></div>');

    const width = 800;
    const height = 1000;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    const popup = window.open("", "_blank", `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`);
    if (popup) {
      popup.document.open();
      popup.document.write(uniHtml);
      popup.document.close();
    } else {
      alert("Por favor permite las ventanas emergentes en tu navegador.");
    }
  };

  return (
    <DataManagement
      columns={columns}
      data={comprobantes}
      title="Comprobantes Emitidos"
      onRowSelectionChange={handleRowSelectionChange}
      actions={[
        !solo_notas_emitidas && (
          <button onClick={handleAplicarAbono} className="btn btn-green btn-sm d-flex me-2 pe-3 rounded-3">
            <Icon className="iconify fs-18px me-2 ms-n1" icon="solar:dollar-bold-duotone" />
            Aplicar abono
          </button>
        ),

        <button onClick={imprimirPdfs} className="btn btn-blue btn-sm d-flex me-2 pe-3 rounded-3">
          <Icon className="iconify fs-18px me-2 ms-n1" icon="solar:refresh-bold-duotone" />
          Imprimir DTE's
        </button>,

        <button onClick={refetch} className="btn btn-theme btn-sm d-flex pe-3 rounded-3">
          <Icon className="iconify fs-18px me-2 ms-n1" icon="solar:refresh-bold-duotone" />
          Refrescar tabla
        </button>,
      ]}
    />
  );
}

export default ComprobantesEmitidos;
